<script lang="ts">
	import type { HorizontalStackedBarChartDataset } from '$lib/components/route-details/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import Icon from '$lib/components/icons/Icon.svelte';

	interface Props {
		datasets: HorizontalStackedBarChartDataset[];
		showBarChart?: boolean;
		classes?: ClassValue;
		description?: Snippet<[{ dataset: HorizontalStackedBarChartDataset }]>;
	}

	const {
		datasets,
		showBarChart = false,
		classes,
		description,
	}: Props = $props();

	const totalNb = $derived(datasets.reduce((acc, dataset) => acc + dataset.nb, 0));

	function getNbPercentage(nb: number) {
		return (nb / totalNb) * 100;
	}

	const datasetsEnriched = $derived(datasets.map((dataset) => {
		return { ...dataset, percent: getNbPercentage(dataset.nb) };
	}));

	const showChartAndColors = $derived(datasetsEnriched.filter(dataset => dataset.nb > 0).length > 1);
</script>

<div class="flex flex-col gap-1 text-md {classes}">
	{#if showChartAndColors && showBarChart}
		<div class="w-full flex gap-0.5 mb-3">
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each datasetsEnriched.filter(dataset => dataset.nb) as dataset}
				<div
					class="h-2 first:rounded-l-1 last:rounded-r-1 {dataset.colorClass ? dataset.colorClass : 'bg-[var(--color)]'}"
					style="width: {dataset.percent}%; --color: {dataset.color};"
				></div>
			{/each}
		</div>
	{/if}
	<!-- eslint-disable-next-line svelte/require-each-key -->
	{#each datasetsEnriched as dataset}
		<div class="costs-grid grid grid-cols-2 text-coloredneutral" style="--color: {dataset.color}">
			<div class="flex items-center gap-1 mb-1.5 ">
				{#if showChartAndColors}
					<div
						class="h-3 w-3 rd-0.5 {dataset.colorClass ? dataset.colorClass : 'bg-[var(--color)]'}"
					></div>
				{/if}
				{dataset.label}
			</div>
			<div class="font-semibold">
				{#if dataset.withCustomDescription}
					{#if description}
						{@render description({ dataset })}
					{:else}
						{dataset.description}
					{/if}
				{:else}
					{dataset.description}
				{/if}
			</div>
		</div>
		{#if dataset?.details?.length}
			<ul class="second-line-one-column p-0 m-0 grid">
				{#each dataset?.details as detail(detail.name)}
					<li class="grid grid-cols-2 text-sm text-coloredneutral-softest mb-1">
						<p class="p-0 pl-4 m-0">
							{detail?.name || ''}

							{#if detail?.message}
								<Icon class="text-md cursor-pointer relative top-.75" icon="info-outlined" tooltipOptions={{ text: detail?.message, bgColor: 'bg-neutral-weakest', textColor: 'text-coloredneutral' }} />
							{/if}
						</p>
						<p class="p-0 pl-4 m-0 text-right">{detail?.cost}</p>
					</li>
				{/each}
			</ul>
		{/if}
	{/each}
</div>
<style>
	.costs-grid {
		& > :nth-child(2) {
			justify-self: end;
		}

		&:last-child {
			margin-bottom: 0;
		}
	}

	.second-line-one-column {
		grid-column: 1 / span 2;

		& > :last-child {
			margin-bottom: 0;
		}
	}
</style>
